import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { MeUser } from '@/api/auth'
import { PRODUCT_BRAND } from '@/brand'

/** The brand a white-label tenant saved, as `/auth/me` and `/tenant/brand` carry it. */
interface TenantBrandValues {
  name: string
  logo_url: string | null
  icon_url: string | null
}

/**
 * The console's brand. MDM42 ships in the build, so the sign-in page and the
 * first paint are already branded. `/auth/me` is the only other source
 * (#222): a tenant the operator white-labelled carries its own name and images
 * there, applied after sign-in and dropped at sign-out, so the next person at
 * this browser never sees the last tenant's brand.
 */
export const useBrandStore = defineStore('brand', () => {
  const name = ref<string>(PRODUCT_BRAND.name)
  const logoUrl = ref<string>(PRODUCT_BRAND.logoUrl)
  const iconUrl = ref<string>(PRODUCT_BRAND.iconUrl)
  /** The section half of the tab title, kept so a brand change re-titles the open page. */
  const section = ref<string | null>(null)

  /** A tenant brand without its own images keeps the MDM42 ones rather than showing none. */
  function apply(brand: TenantBrandValues): void {
    name.value = brand.name
    logoUrl.value = brand.logo_url ?? PRODUCT_BRAND.logoUrl
    iconUrl.value = brand.icon_url ?? PRODUCT_BRAND.iconUrl
    refresh()
  }

  /**
   * What `/auth/me` says: the tenant's brand when white label is on and a
   * brand has been saved, else the deployment brand. Anything short of both
   * resets, so a flag switched off or a tenant that never saved one is never
   * left wearing a stale brand from the previous session.
   */
  function applyFromMe(me: Pick<MeUser, 'white_label' | 'brand'>): void {
    if (me.white_label === true && me.brand !== null) {
      apply(me.brand)
    } else {
      reset()
    }
  }

  /** Back to the brand in the build: sign-out, and any session that is not a white-label tenant's. */
  function reset(): void {
    name.value = PRODUCT_BRAND.name
    logoUrl.value = PRODUCT_BRAND.logoUrl
    iconUrl.value = PRODUCT_BRAND.iconUrl
    refresh()
  }

  function refresh(): void {
    applyFavicon(iconUrl.value)
    if (section.value !== null) document.title = `${section.value} · ${name.value}`
  }

  /** The tab icon follows the brand; index.html carries the MDM42 one. */
  function applyFavicon(url: string): void {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (link === null) throw new Error('index.html is missing its <link rel="icon">')
    link.href = url
  }

  /** "Devices · MDM42" in the tab. */
  function setPageTitle(title: string): void {
    section.value = title
    document.title = `${title} · ${name.value}`
  }

  return {
    // Returned so Pinia tracks them: devtools, $patch and $reset read state
    // through this object.
    name,
    logoUrl,
    iconUrl,
    apply,
    applyFromMe,
    reset,
    setPageTitle,
  }
})
